import { faClock } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatCurrency, pluralize } from "@gymflow/helpers";
import { AppointmentDTO } from "@gymflow/types";
import { useClubSettings } from "apps/portal/src/providers";
import { DateTime } from "luxon";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { usePortalRoutes } from "../../../hooks/usePortalRoutes";
import { CalendarDateIcon } from "../../atoms/icons/CalendarDateIcon";
import DashedIcon from "../../atoms/icons/DashedIcon";
import { AppointmentPaymentStatusBadge } from "../../Badges/AppointmentPaymentStatusBadge";
import { BookingStatusAppointmentBadge } from "../../Badges/BookingStatusAppointmentBadge";
import { FacilityAvatar } from "../FacilityAvatar";
import { StaffAvatar } from "../StaffAvatar";

// appointment date/time with timezone (because of useAppointment)
export function AppointmentSummary({
  appointment,
}: {
  appointment: AppointmentDTO;
}) {
  const { t } = useTranslation();
  const { club } = useClubSettings();
  const { createMemberLink } = usePortalRoutes();

  const price = useMemo(() => {
    if (appointment.pricingModel === "FREE") {
      return t("components.sidebarAppointment.appointmentSummary.free");
    }
    if (appointment.finalPrice !== undefined) {
      return formatCurrency(appointment.finalPrice, club.defaultCurrency);
    }
    return "";
  }, [
    appointment.finalPrice,
    appointment.pricingModel,
    club.defaultCurrency,
    t,
  ]);

  const initials = `${appointment.appointmentUser.firstName[0]}${appointment.appointmentUser.lastName[0]}`;

  return (
    <div className="flex flex-col gap-6 px-6 py-2">
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1.5">
          {appointment.appointmentHost && (
            <StaffAvatar
              size="big"
              userStaff={{
                firstName: appointment.appointmentHost.firstName,
                lastName: appointment.appointmentHost.lastName,
                picture: appointment.appointmentHost?.picture,
              }}
            />
          )}
          {appointment?.startDate && (
            <div className="flex flex-row items-center gap-2">
              <CalendarDateIcon
                className="size-6"
                pathClassName="stroke-gray-500 dark:stroke-gray-400"
              />
              <div className="dark:text-gray-25 line-clamp-1 text-base font-medium text-gray-950">
                {DateTime.fromISO(appointment.startDate).toLocaleString({
                  weekday: "long",
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                })}
              </div>
            </div>
          )}
          {appointment?.startDate && appointment?.endDate && (
            <div className="flex flex-row items-center gap-2.5 pl-0.5">
              <FontAwesomeIcon
                className="size-5 text-gray-500 dark:text-gray-400"
                icon={faClock}
              />
              <div className="line-clamp-1 text-sm font-semibold text-gray-950">
                {`${DateTime.fromISO(appointment.startDate).toLocaleString(
                  DateTime.TIME_SIMPLE,
                )} - ${DateTime.fromISO(appointment.endDate).toLocaleString(
                  DateTime.TIME_SIMPLE,
                )}`}
              </div>
            </div>
          )}
          {appointment?.appointmentFacility && (
            <FacilityAvatar
              size="big"
              showLocationIcon
              facility={{
                name: appointment.appointmentFacility.name,
                picture: appointment.appointmentFacility.picture,
              }}
            />
          )}
        </div>
        <div className="shadow-xs flex flex-col gap-2.5 rounded-2xl border border-gray-200 p-3">
          <BookingStatusAppointmentBadge appointment={appointment} />
          <div className="flex flex-row items-center gap-3">
            <div className="flex size-10 items-center justify-center overflow-hidden rounded-full border border-gray-200 bg-gray-100">
              <div className="flex size-full items-center justify-center text-base font-semibold text-gray-950">
                {initials}
              </div>
            </div>
            <div className="flex flex-col">
              <div className="text-sm font-normal text-gray-500">
                {t(
                  "components.sidebarAppointment.appointmentSummary.participant",
                )}
              </div>
              <Link
                className="text-base font-semibold text-gray-950"
                to={createMemberLink(appointment.appointmentUser.id)}
              >{`${appointment.appointmentUser.firstName} ${appointment.appointmentUser.lastName}`}</Link>
            </div>
          </div>
        </div>
      </div>
      <DashedIcon />
      <div className="flex flex-col gap-1.5">
        <div className="flex flex-row items-center justify-between">
          <AppointmentPaymentStatusBadge appointment={appointment} />
          {appointment?.finalSessionCost && (
            <div className="text-sm font-medium text-gray-500">
              {`${appointment.finalSessionCost} ${pluralize(t("components.sidebarAppointment.appointmentSummary.credit"), t("components.sidebarAppointment.appointmentSummary.credits"), appointment.finalSessionCost)}`}
            </div>
          )}
        </div>
        {price && (
          <div className="flex flex-row items-center justify-between">
            <div className="text-xl font-bold text-gray-950">
              {t("components.sidebarAppointment.appointmentSummary.amountDue")}
            </div>
            <div className="text-xl font-bold text-gray-950">{price}</div>
          </div>
        )}
      </div>
    </div>
  );
}
